'use client';

/**
 * ObeyaPlanLensConcentrationCard — the Pareto principle over selected
 * spend: products ranked with a running share. A renderer: the bar widths
 * (paint.bar_pct), the vital-few membership and the row that ends the group
 * (ends_vital_few) all arrive from the BFF; nothing is derived here.
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — ObeyaPlanLensConcentrationCard
 * @see klyde_edge/klyde_intelligence/projects/plan_lenses.py
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { PlanLensFrame } from './plan-lens-frame';
import { usePlanLenses, money } from './use-plan-lenses';

export function ObeyaPlanLensConcentrationCard() {
  const { contentKey } = useCardIdentity();
  const { mcpId, strategy, d, isLoading, queryKey } = usePlanLenses();

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_plan_lens_concentration',
    status: (d ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: queryKey,
    text: d ? d.labels.concentration_title : 'Spend concentration',
    json: d ? d.concentration : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, d, mcpId, strategy]));

  if (!mcpId) return null;
  if (isLoading && !d) return <div className={cn(text.muted, 'p-6 text-center')}>…</div>;
  if (!d) return null;

  const L = d.labels;
  const c = d.concentration.data;

  return (
    <PlanLensFrame title={L.concentration_title} meta={L.concentration_meta}>
      <div className="flex flex-col gap-4">
      <div className="flex flex-col">
        {c.products.map((p, i) => (
          <div key={`${p.product_name}-${i}`}>
            <div className={cn('flex items-center gap-3 py-1.5', !p.in_vital_few && 'opacity-60')}>
              <span className={cn(text.body, 'w-[34%] truncate')}>{p.product_name ?? L.unlabelled}</span>
              <div className="flex-1 h-2.5 rounded bg-muted/40">
                <div className="h-full rounded bg-[#7fa2ff]" style={{ width: `${p.paint.bar_pct ?? 0}%` }} />
              </div>
              <span className={cn(text.muted, 'w-[15%] text-right tabular-nums')}>{money(p.selected_total)}</span>
              <span className={cn(text.muted, 'w-[7%] text-right tabular-nums')}>{p.share_pct}%</span>
              <span className={cn(text.muted, 'w-[14%] text-right tabular-nums')}>{L.cumulative} {p.cumulative_share_pct}%</span>
            </div>
            {p.ends_vital_few && <div className="border-t-2 border-amber-500/80 my-1" />}
          </div>
        ))}
      </div>
      <div className={cn(text.muted)}>{L.cut_rule_note} {c.vital_few_boundary_pct}%</div>
      </div>
    </PlanLensFrame>
  );
}
